import {
    Component,
    Injector,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FiltersDialogComponent } from './filters-dialog/filters-dialog.component';
import { FilterStateService } from './filters-dialog/filter-state.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LogsService } from './services/logs.service';
import { BufferProgressBarComponent } from '../../ui-components/buffer-progress-bar/buffer-progress-bar.component';
import { Timestamp } from '@ngx-grpc/well-known-types';
import { LogTransferService } from './services/log-transfer.service';
import { LogRequest, LogResponse } from '../../protos/generated/admin.pb';

@Component({
    selector: 'app-developer-logs',
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        CommonModule,
        MatProgressBarModule,
        RouterLink,
        BufferProgressBarComponent
    ],
    templateUrl: './developer-logs.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './developer-logs.component.scss'
})
export class DeveloperLogsComponent implements OnInit {
    displayedColumns: string[] = [
        'ID',
        'severity',
        'method',
        'message',
        'requestPath',
        'time',
        'action'
    ];
    dataSource = new MatTableDataSource<LogResponse>([]);
    logRequest = new LogRequest();
    selectedSeverity: string = '';
    isLoading: boolean = false;
    totalLogs: number = 0;
    currentPage: number = 1;
    pageSize: number = 8;
    pageSizeOptions: number[] = [8, 20, 30];
    @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

    constructor(
        private logsService: LogsService,
        private dialog: MatDialog,
        private injector: Injector,
        private filterStateService: FilterStateService,
        private route: ActivatedRoute,
        private router: Router,
        private logTransferService: LogTransferService
    ) {}

    ngOnInit() {
        this.route.queryParams.subscribe(params => {
            localStorage;
            const filter = new LogRequest();
            if (params['severity']) filter.severity = +params['severity'];
            if (params['sourceContext'])
                filter.sourceContext = params['sourceContext'];
            if (params['method']) filter.method = params['method'];
            if (params['filter']) filter.filter = params['filter'];
            if (params['id']) filter.id = +params['id'];
            if (params['startTime']) {
                const ts = new Timestamp();
                ts.seconds = params['startTime'];
                ts.nanos = 0;
                filter.startTime = ts;
            }

            if (params['endTime']) {
                const ts = new Timestamp();
                ts.seconds = params['endTime'];
                ts.nanos = 0;
                filter.endTime = ts;
            }

            this.logRequest = filter;
            this.filterStateService.setFilterState(filter);
            this.fetchLogs(filter, this.currentPage, this.pageSize);
        });
    }
    fetchLogs(logRequest: LogRequest, page: number, pageSize: number) {
        this.isLoading = true;
        this.dataSource.data = [];
        logRequest.pageNumber = page;
        logRequest.pageSize = pageSize;

        this.logsService.getLogs(logRequest).subscribe({
            next: (result: { logs: LogResponse[]; total: number }) => {
                this.dataSource.data = result.logs;
                this.totalLogs = result.total;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }
    openFiltersDialog() {
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();

        const currentFilters = this.filterStateService.getFilterState();

        const dialogRef = this.dialog.open(FiltersDialogComponent, {
            width: '1000px',
            height: 'auto',
            data: { logRequest: { ...currentFilters } },
            injector: this.injector,
            hasBackdrop: true,
            disableClose: true,
            ariaLabel: 'Logout confirmation dialog'
        });

        dialogRef.afterClosed().subscribe((updatedFilters: LogRequest) => {
            if (updatedFilters) {
                this.applyFilters(updatedFilters);
            }
        });
    }
    public truncateString(str: string) {
        if (str.length > 40) {
            return str.substring(0, 40) + '...';
        } else {
            return str;
        }
    }
    onPageChange(event: any) {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;
        this.fetchLogs(this.logRequest, this.currentPage, this.pageSize);
    }
    applyFilters(filter: LogRequest) {
        this.filterStateService.setFilterState(filter);
        this.logRequest = filter;
        this.currentPage = 1;
        const queryParams: any = {
            severity: filter.severity,
            sourceContext: filter.sourceContext,
            method: filter.method,
            filter: filter.filter,
            id: filter.id,
            startTime: filter.startTime?.seconds,
            endTime: filter.endTime?.seconds
        };
        this.router
            .navigate([], {
                relativeTo: this.route,
                queryParams,
                queryParamsHandling: 'merge'
            })
            .then(() => {
                this.fetchLogs(filter, this.currentPage, this.pageSize);
            });
    }
    openLogDetails(log: LogResponse) {
        this.logTransferService.setLog(log);
        this.router.navigate(['/others/developer-logs', log.id], {
            state: { log }
        });
    }
}
